// Export formatting for the cross-client GBP worklist.
//
// Kept out of the component so the exact wire format is testable and so the
// Markdown and JSON paths cannot drift apart on what they include.
//
// The Markdown form is the one meant to be pasted into an AI chat: headings
// carry the client identity so a model reading 40 clients in one blob never
// has to guess which business a paste block belongs to.

import type { GbpActionsClientDto, GbpActionDto } from './api';

export interface ExportOptions {
  includeAnalysis: boolean;
  includeActions: boolean;
  /** 'all' keeps both buckets; the others narrow to one owner. */
  owner: 'all' | 'auto' | 'approve';
  /**
   * 'remaining' — not yet actioned, plus anything whose advice was rewritten
   * after it was marked (that is live work again). This is the default because
   * the usual question is "what is still outstanding".
   * 'done' — only what has been marked done or skipped.
   * 'all' — everything, regardless of mark.
   */
  status: 'remaining' | 'done' | 'all';
}

/** Marked, and the advice has not moved on since — i.e. genuinely settled. */
export function isSettled(a: GbpActionDto): boolean {
  return !!a.progress && !a.progress.changedSinceMarked;
}

export function fmtDate(ms: number | null): string {
  if (!ms) return '—';
  return new Date(ms).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' });
}

export function clientLabel(c: GbpActionsClientDto): string {
  return c.locationLabel ? `${c.name} — ${c.locationLabel}` : c.name;
}

export function visibleActions(
  c: GbpActionsClientDto,
  owner: ExportOptions['owner'],
  status: ExportOptions['status'] = 'all',
): GbpActionDto[] {
  return c.actions.filter(a => {
    if (owner !== 'all' && a.owner !== owner) return false;
    if (status === 'remaining') return !isSettled(a);
    if (status === 'done') return isSettled(a);
    return true;
  });
}

function bullets(label: string, items: string[]): string {
  if (!items.length) return '';
  return `**${label}**\n${items.map(i => `- ${i}`).join('\n')}\n\n`;
}

function analysisMd(c: GbpActionsClientDto): string {
  const a = c.analysis;
  if (!a) return '_No analysis stored for this client yet._\n\n';
  let out = '';
  if (a.executiveSummary) out += `**Executive summary**\n${a.executiveSummary}\n\n`;
  if (a.diagnosis) {
    if (a.diagnosis.problem) out += `**Diagnosis — problem**\n${a.diagnosis.problem}\n\n`;
    if (a.diagnosis.opportunity) out += `**Diagnosis — opportunity**\n${a.diagnosis.opportunity}\n\n`;
  }
  out += bullets('Gaps', a.gaps);
  if (a.profileInsight) out += `**Profile insight** (${a.profileInsight.tone})\n${a.profileInsight.text}\n\n`;
  if (a.mapsInsight) out += `**Map insight** (${a.mapsInsight.tone})\n${a.mapsInsight.text}\n\n`;
  if (a.aiInsight) out += `**Answer-engine insight**\n${a.aiInsight}\n\n`;
  out += bullets('Competitor insights', a.competitorInsights);
  if (a.vulnerableCompetitors.length) {
    out += `**Vulnerable competitors**\n${a.vulnerableCompetitors
      .map(v => `- ${v.name} — weakness: ${v.weakness} → exploit: ${v.howToExploit}`)
      .join('\n')}\n\n`;
  }
  out += bullets('Website insights', a.websiteInsights);
  out += bullets('Optional notes (deliberately not actioned)', a.optionalNotes);
  return out;
}

function actionMd(a: GbpActionDto, index: number): string {
  const flag = a.owner === 'approve' ? '  **[NEEDS APPROVAL]**' : '';
  const scores = [
    a.impact != null ? `Impact ${a.impact}` : null,
    a.effort != null ? `Effort ${a.effort}` : null,
    a.risk != null ? `Risk ${a.risk}` : null,
  ].filter(Boolean).join(' · ');

  let out = `#### ${index}. ${a.title}${flag}\n`;
  if (a.progress) {
    // Carried into the export so a reader (or a model) is never told to redo
    // work that is already applied, and can see when advice has since changed.
    const when = fmtDate(a.progress.markedAt);
    out += a.progress.changedSinceMarked
      ? `- Status: marked ${a.progress.status} on ${when}, but the recommendation has changed since — treat as outstanding\n`
      : `- Status: ${a.progress.status} (${when})\n`;
  }
  if (a.field) out += `- Field: \`${a.field}\`\n`;
  if (a.where) out += `- Where: ${a.where}\n`;
  if (scores) out += `- ${scores}\n`;
  if (a.current) out += `- Current: ${a.current}\n`;
  if (a.target) out += `- Target: ${a.target}\n`;
  if (a.why) out += `- Why: ${a.why}\n`;
  if (a.pasteBody) {
    // Fenced so an AI (and a human) can tell the literal paste text apart from
    // the prose describing it. pasteBody is spec'd as zero-edit copy.
    out += `\n**Paste — ${a.pasteLabel ?? (a.field || 'value')}:**\n\`\`\`\n${a.pasteBody}\n\`\`\`\n`;
  }
  return out + '\n';
}

export function buildMarkdown(clients: GbpActionsClientDto[], opts: ExportOptions): string {
  const stamp = new Date().toLocaleString('en-GB', {
    day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
  });
  const parts: string[] = [];
  const included = [
    opts.includeAnalysis ? 'Data & Analysis' : null,
    opts.includeActions ? 'Actions' : null,
  ].filter(Boolean).join(' + ');
  const ownerNote = opts.owner === 'all' ? 'all owners'
    : opts.owner === 'auto' ? 'auto only (safe to apply)'
      : 'needs-approval only';
  const statusNote = opts.status === 'remaining' ? 'still outstanding'
    : opts.status === 'done' ? 'already done or skipped'
      : 'any status';

  parts.push(`# GBP worklist export — ${clients.length} client${clients.length === 1 ? '' : 's'}\n`);
  parts.push(`Exported ${stamp} · Includes: ${included || 'nothing selected'} · Actions filtered to: ${ownerNote}, ${statusNote}\n`);
  parts.push('Reviews are tracked separately and are not part of this export.\n');
  parts.push('\n---\n\n');

  for (const c of clients) {
    const acts = visibleActions(c, opts.owner, opts.status);
    parts.push(`## ${clientLabel(c)} — ${c.location}\n`);
    const meta = [
      c.score != null ? `Health score ${c.score}/100` : null,
      `Report generated ${fmtDate(c.generatedAt)}`,
      c.category ? `Category ${c.category}` : null,
    ].filter(Boolean).join(' · ');
    parts.push(`${meta}\n\n`);

    if (!c.hasReport) {
      parts.push('_No GBP Optimization report generated for this client yet._\n\n---\n\n');
      continue;
    }

    if (opts.includeAnalysis) {
      parts.push('### Data & Analysis\n\n');
      parts.push(analysisMd(c));
    }

    if (opts.includeActions) {
      const autoN = acts.filter(a => a.owner === 'auto').length;
      const apprN = acts.length - autoN;
      parts.push(`### Actions — ${acts.length} total (${autoN} auto · ${apprN} need approval)\n\n`);
      if (!acts.length) {
        parts.push('_No actions match the current filter._\n\n');
      } else {
        acts.forEach((a, i) => parts.push(actionMd(a, i + 1)));
      }
      if (c.suggestions.length) {
        parts.push(`**Client/dev suggestions (outside the profile)**\n${c.suggestions
          .map(s => `- ${s.title}${s.impact != null ? ` (impact ${s.impact})` : ''} — ${s.why}`)
          .join('\n')}\n\n`);
      }
    }

    parts.push('---\n\n');
  }

  return parts.join('');
}

export function buildJson(clients: GbpActionsClientDto[], opts: ExportOptions): string {
  const payload = {
    exportedAt: new Date().toISOString(),
    includes: {
      analysis: opts.includeAnalysis,
      actions: opts.includeActions,
      reviews: false, // reviews live in their own report tab, never in this export
    },
    ownerFilter: opts.owner,
    statusFilter: opts.status,
    clientCount: clients.length,
    clients: clients.map(c => ({
      clientId: c.clientId,
      name: c.name,
      locationLabel: c.locationLabel,
      location: c.location,
      category: c.category,
      score: c.score,
      generatedAt: c.generatedAt,
      hasReport: c.hasReport,
      ...(opts.includeAnalysis ? { analysis: c.analysis } : {}),
      ...(opts.includeActions
        ? { actions: visibleActions(c, opts.owner, opts.status), suggestions: c.suggestions }
        : {}),
    })),
  };
  return JSON.stringify(payload, null, 2);
}

export function downloadText(filename: string, text: string, mime: string) {
  const url = URL.createObjectURL(new Blob([text], { type: `${mime};charset=utf-8` }));
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  // Revoke on the next frame — revoking synchronously can cancel the download
  // in Safari before it has read the blob.
  requestAnimationFrame(() => URL.revokeObjectURL(url));
}

export async function copyText(text: string): Promise<boolean> {
  try {
    await navigator.clipboard.writeText(text);
    return true;
  } catch {
    // Clipboard API needs a secure context and permission; fall back to the
    // legacy path so the button still works on an internal HTTP origin.
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.opacity = '0';
      document.body.appendChild(ta);
      ta.select();
      const ok = document.execCommand('copy');
      ta.remove();
      return ok;
    } catch {
      return false;
    }
  }
}
